import { useEffect, useRef } from 'react';

/**
 * Scoped keyboard shortcuts.
 *
 * Two rules are baked in so no caller has to remember them:
 *
 *   1. NOTHING fires while a Dialog or Sheet is open. Every modal in the
 *      app carries `role="dialog"`, so one DOM query settles it. Without
 *      this, Esc-to-go-home would fire *behind* the "leave the game?"
 *      confirm the user just opened, which is the exact opposite of what
 *      they asked for.
 *   2. NOTHING fires while an input has focus — unless the hotkey opts in
 *      with `inInput: true`. Enter-in-a-race-field is the one that has to;
 *      Space-to-skip-a-beat emphatically must not, or you can't type a
 *      space.
 */
export interface HotkeySpec {
  /** A KeyboardEvent.key value, or several. e.g. 'Enter', ' ', 'ArrowLeft'. */
  keys: string | string[];
  handler: (e: KeyboardEvent) => void;
  /** Fire even while a text input / textarea has focus. Default false. */
  inInput?: boolean;
  /** Fire even while a dialog or sheet is mounted. Default false. */
  inDialog?: boolean;
  /** Skip this one without changing the shape of the array. Default true. */
  enabled?: boolean;
  /** preventDefault() on match. Default true. */
  preventDefault?: boolean;
}

function isTextEntry(el: Element | null): boolean {
  if (!el) return false;
  const tag = el.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  return (el as HTMLElement).isContentEditable === true;
}

function dialogIsOpen(): boolean {
  if (typeof document === 'undefined') return false;
  return !!document.querySelector('[role="dialog"], [aria-modal="true"]');
}

/**
 * @param specs   the shortcut map. Recreated every render is fine — it's
 *                held in a ref, so the listener is bound exactly once.
 * @param enabled master switch (e.g. `surface === 'desktop'`).
 */
export function useHotkeys(specs: HotkeySpec[], enabled = true): void {
  const ref = useRef(specs);
  ref.current = specs;

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return;

    const onKey = (e: KeyboardEvent) => {
      // A modifier means the user is talking to the browser, not to us.
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      const inInput = isTextEntry(document.activeElement);
      const inDialog = dialogIsOpen();

      for (const spec of ref.current) {
        if (spec.enabled === false) continue;
        const keys = Array.isArray(spec.keys) ? spec.keys : [spec.keys];
        if (!keys.includes(e.key)) continue;
        if (inInput && !spec.inInput) continue;
        if (inDialog && !spec.inDialog) continue;
        if (spec.preventDefault !== false) e.preventDefault();
        spec.handler(e);
        return; // first match wins
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enabled]);
}
